import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, getDocs, onSnapshot, limit, Timestamp, doc, getDoc, setDoc, getCountFromServer } from 'firebase/firestore';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { db } from '../firebase';
import ReportsModule from './ReportsModule';
import SettingsModule from '../components/SettingsModule';
import SupervisorClockMonitor from './SupervisorClockMonitor';
import SupervisorJourneyMonitor from './SupervisorJourneyMonitor';
import SupervisorOutletManager from './SupervisorOutletManager';
import SupervisorSparesMonitor from './SupervisorSparesMonitor';
import SupervisorEquipmentManager from './SupervisorEquipmentManager';
import ServicePhotoViewer from './ServicePhotoViewer';
import AdminUserManager from './AdminUserManager';
import AccessRequests from './AccessRequests';
import TransferManager from './TransferManager';
import SparePartsInventory from './SparePartsInventory';
import OrgStructure from './OrgStructure';
import OperationsIntelligence from './OperationsIntelligence';
import SupervisorMonitor from './SupervisorMonitor'; // ✅ NEW: Supervisor tracking for admins
import NotificationBell from '../components/NotificationBell';
import SupervisorMessaging from '../components/SupervisorMessaging';
import { notifyPhotoFlagged } from '../utils/NotificationService';
import { openInGoogleMaps, formatCoordinates } from '../utils/locationTracker';
import { isAdminOrHigher, isSuperAdmin } from '../services/authService'; // ✅ NEW: Role helpers
import { SEV, runRules } from '../utils/intelligenceEngine'; // ✅ NEW: shared with OperationsIntelligence.js
import './SupervisorDashboard.css';
import './DionysusDashboard.css'; // ✅ NEW: Dionysus-style shell (sidebar/widgets/chart/table) — scoped under .dio-
import {
  FaTachometerAlt, FaUsers, FaClock, FaRoute, FaBullseye, FaTools, FaChartLine,
  FaLightbulb, FaCogs, FaStore, FaExchangeAlt, FaWrench, FaBoxes, FaComments,
  FaCog, FaSitemap, FaKey, FaUserShield, FaMapMarkedAlt, FaShieldAlt,
  FaBars, FaSearch, FaTimes, FaArrowLeft, FaSignOutAlt, FaFireExtinguisher,
  FaUser, FaCamera, FaFlag, FaMapMarkerAlt, FaStar, FaCrown, FaSync,
  FaClipboardList,
} from 'react-icons/fa';

function SupervisorDashboard({ user, onBack, onLogout, onUserUpdate }) {
  const isAdmin = isAdminOrHigher(user); // ✅ Updated to use helper function

  const [activeTab, setActiveTab] = useState('overview');
  const [services, setServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState({ totalServices: 0, completedToday: 0, inProgress: 0, pending: 0, activeTechnicians: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Photo viewer
  const [viewingPhotos, setViewingPhotos] = useState(null);
  const [servicesFilter, setServicesFilter] = useState('all');

  // System logs (admin)
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // ✅ NEW: Business Pulse panel (Needs Attention / Staff on Duty /
  // Monthly Target / 7-day performance) — replaces the old static-only
  // stat cards with live, actionable data.
  const [clockEvents, setClockEvents] = useState([]);
  const [pulseFlags, setPulseFlags] = useState([]);
  const [staffOnDuty, setStaffOnDuty] = useState({ onDuty: 0, total: 0 });
  const [dailyCounts, setDailyCounts] = useState([]);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [monthlyTarget, setMonthlyTarget] = useState(null); // null = still loading
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);

  // ✅ MOVED (fix): these two hooks + the resize useEffect used to be declared
  // further down, AFTER several early `return`s (activeTab === 'clock-monitor',
  // viewingPhotos, etc). That breaks the Rules of Hooks — hooks must run in the
  // same order on every render, so they can never sit after a conditional
  // return. Moved up here with the rest of the component's hooks.
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 768);
  const [navSearch, setNavSearch] = useState('');

  useEffect(() => {
    const handleResize = () => {
      // Only auto-close when crossing down onto a small screen — never
      // force it open on desktop if the user deliberately collapsed it.
      if (window.innerWidth <= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // ✅ FIXED: this used to be onSnapshot(ALL service records, no bound),
    // then filter down to "last 7 days" in the browser AFTER downloading
    // everything. At real scale (Dionysus is tracking ~19,000 services a
    // month) that means downloading months or years of history on every
    // dashboard load, and keeping a live listener open on the entire
    // collection forever — every write to ANY record, even years old,
    // would re-trigger this for every supervisor with the dashboard open.
    // Now the date filter happens server-side, plus a hard cap as a
    // safety net regardless of how much a single week ever contains.
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    lastWeek.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, 'serviceRecords'),
      where('createdAt', '>=', Timestamp.fromDate(lastWeek)),
      orderBy('createdAt', 'desc'),
      limit(2000)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const filtered = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Note: allServices is now "last 7 days," not literally everything
      // ever recorded — the admin locations-map tab (which reads from
      // allServices) now shows recent activity rather than full history.
      // A true full-history view would need its own separately paginated
      // query, not a live listener like this one.
      setAllServices(filtered);
      setServices(filtered);
      calculateStats(filtered);
      setLastUpdate(new Date());
    }, () => alert('Unable to load dashboard data.'));

    loadTechnicians();
    if (isAdmin) loadAllUsers();
    setLoading(false);
    return () => unsubscribe();
  }, []);

  // ✅ NEW: Auto-load logs when system-logs tab is opened
  useEffect(() => {
    if (activeTab === 'system-logs' && isAdmin && logs.length === 0) {
      loadLogs();
    }
  }, [activeTab]);

  const loadTechnicians = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'technician'), where('active', '==', true)));
      setTechnicians(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {}
  };

  const loadAllUsers = async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {}
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      // ✅ FIXED: was fetching the ENTIRE locationLogs collection — the
      // fastest-growing collection in the app, since every active
      // technician writes to it every 5 minutes — just to keep the first
      // 200 after the fact. Limit now applied server-side instead.
      const snap = await getDocs(
        query(collection(db, 'locationLogs'), orderBy('timestamp', 'desc'), limit(200))
      );
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading logs:', e);
    } finally {
      setLogsLoading(false);
    }
  };

  // ✅ NEW: live clock events for the last 7 days — feeds both the
  // Intelligence rule engine (attendance-vs-output rule) and the
  // Staff on Duty widget below. Same collection/bound pattern as the
  // allServices listener above.
  useEffect(() => {
    const day7 = new Date();
    day7.setDate(day7.getDate() - 7);
    day7.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, 'clockEvents'),
      where('timestamp', '>=', day7.getTime()),
      orderBy('timestamp', 'desc'),
      limit(3000)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setClockEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, []);

  // ✅ NEW: recompute Intelligence flags whenever the underlying live data
  // changes. Uses the exact same rule engine as the full Intelligence
  // page (src/utils/intelligenceEngine.js) — this list will always agree
  // with what that page shows, since both call the same function.
  useEffect(() => {
    if (technicians.length === 0 && allServices.length === 0 && clockEvents.length === 0) return;
    const result = runRules({ services: allServices, clockEvents, users: technicians });
    setPulseFlags(result.filter(f => f.severity === SEV.CRITICAL || f.severity === SEV.HIGH));
  }, [allServices, clockEvents, technicians]);

  // ✅ NEW: who's clocked in right now, today, vs total registered
  // technicians — derived from the same live clockEvents data above.
  useEffect(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const todayEvents = clockEvents
      .filter(c => new Date(c.timestamp) >= todayStart)
      .sort((a, b) => a.timestamp - b.timestamp);

    const latestByUser = {};
    todayEvents.forEach(c => { latestByUser[c.userId] = c; });

    const onDutyCount = Object.values(latestByUser).filter(c => c.type === 'in').length;
    setStaffOnDuty({ onDuty: onDutyCount, total: technicians.length });
  }, [clockEvents, technicians]);

  // ✅ NEW: last-7-days daily service counts for the performance chart —
  // built entirely from the allServices data already being live-streamed
  // above, no extra Firestore reads needed.
  useEffect(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      days.push({ date: d, isToday: i === 0 });
    }
    const counts = days.map(({ date, isToday }) => {
      const dayEnd = new Date(date.getTime() + 86400000);
      const count = allServices.filter(s => {
        const sd = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
        return sd >= date && sd < dayEnd;
      }).length;
      return { label: date.toLocaleDateString('en-KE', { weekday: 'short' }), count, isToday };
    });
    setDailyCounts(counts);
  }, [allServices]);

  // ✅ NEW: monthly target progress. Uses getCountFromServer for the
  // month-to-date count instead of downloading every record just to
  // count them, and reads the admin-editable target from a small
  // `orgSettings/global` doc (created on first save).
  useEffect(() => {
    loadMonthlyPulse();
  }, []);

  const loadMonthlyPulse = async () => {
    try {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const [countSnap, settingsSnap] = await Promise.all([
        getCountFromServer(query(
          collection(db, 'serviceRecords'),
          where('createdAt', '>=', Timestamp.fromDate(monthStart))
        )),
        getDoc(doc(db, 'orgSettings', 'global'))
      ]);

      setMonthlyCount(countSnap.data().count);
      setMonthlyTarget(settingsSnap.exists() ? (settingsSnap.data().monthlyServiceTarget || 0) : 0);
    } catch (e) {
      console.error('Error loading monthly pulse:', e);
      setMonthlyTarget(0);
    }
  };

  // ✅ NEW: Dionysus-style dashboard widgets/chart/table data.
  // Counts use getCountFromServer (no doc downloads); the chart pulls one
  // aggregation query over a rolling 31-day window of keg-pump service
  // records — separate from the 7-day `allServices` listener above since
  // it needs a longer, one-time range rather than a live subscription.
  // NOTE: this range query (equipmentType == + createdAt range, both on
  // `serviceRecords`) needs a Firestore composite index; Firestore will
  // log a console error with a direct "create index" link the first time
  // this runs against real data if the index doesn't exist yet.
  const [dioStats, setDioStats] = useState({ totalPumps: 0, totalOutlets: 0, totalUsers: 0, servicedPumpsRange: 0, todayPumpsServiced: 0 });
  const [dioChart, setDioChart] = useState({ categories: [], counts: [], rangeStartLabel: '', rangeEndLabel: '' });
  const [dioLoading, setDioLoading] = useState(true);

  useEffect(() => {
    loadDionysusStats();
  }, []);

  const loadDionysusStats = async () => {
    setDioLoading(true);
    try {
      const RANGE_DAYS = 31; // matches Dionysus's ~1-month "Jul-24 to Aug-23" window
      const rangeStart = new Date();
      rangeStart.setDate(rangeStart.getDate() - (RANGE_DAYS - 1));
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date();
      rangeEnd.setHours(23, 59, 59, 999);

      const [pumpsSnap, outletsSnap, usersSnap, rangeServicesSnap] = await Promise.all([
        getCountFromServer(query(collection(db, 'equipment'), where('equipmentType', '==', 'keg_pump'))),
        getCountFromServer(collection(db, 'outlets')),
        getCountFromServer(collection(db, 'users')),
        getDocs(query(
          collection(db, 'serviceRecords'),
          where('equipmentType', '==', 'keg_pump'),
          where('createdAt', '>=', Timestamp.fromDate(rangeStart)),
          where('createdAt', '<=', Timestamp.fromDate(rangeEnd))
        ))
      ]);

      // One bucket per calendar day across the range
      const buckets = [];
      for (let i = 0; i < RANGE_DAYS; i++) {
        const d = new Date(rangeStart);
        d.setDate(d.getDate() + i);
        buckets.push({ day: d, count: 0 });
      }
      rangeServicesSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const d = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        d.setHours(0, 0, 0, 0);
        const idx = Math.round((d.getTime() - rangeStart.getTime()) / 86400000);
        if (idx >= 0 && idx < buckets.length) buckets[idx].count++;
      });

      const fmt = (d) => `${d.toLocaleDateString('en-US', { month: 'short' })}-${d.getFullYear()}-${String(d.getDate()).padStart(2, '0')}`;

      setDioChart({
        categories: buckets.map(b => String(b.day.getDate()).padStart(2, '0')),
        counts: buckets.map(b => b.count),
        rangeStartLabel: fmt(rangeStart),
        rangeEndLabel: fmt(rangeEnd)
      });

      setDioStats({
        totalPumps: pumpsSnap.data().count,
        totalOutlets: outletsSnap.data().count,
        totalUsers: usersSnap.data().count,
        servicedPumpsRange: rangeServicesSnap.size,
        todayPumpsServiced: buckets[buckets.length - 1]?.count || 0
      });
    } catch (e) {
      console.error('Error loading Dionysus dashboard stats:', e);
    } finally {
      setDioLoading(false);
    }
  };

  // Per-technician "today" rollup for the Technicians table — derived from
  // the existing 7-day `services` listener (today always falls inside it),
  // so no extra query is needed. Only technicians with 1+ pump service
  // today are shown, matching the reference table's behaviour.
  const dioTechToday = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const map = {};
    services.forEach(s => {
      if (!s.createdAt) return;
      const d = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
      if (d < today) return;
      if (!map[s.technicianId]) {
        map[s.technicianId] = { id: s.technicianId, name: s.technicianName || 'Unknown', outlets: new Set(), pumps: 0 };
      }
      if (s.outletName) map[s.technicianId].outlets.add(s.outletName);
      if (s.equipmentType === 'keg_pump') map[s.technicianId].pumps++;
    });
    return Object.values(map)
      .map(t => ({ id: t.id, name: t.name, outlets: t.outlets.size, pumps: t.pumps }))
      .filter(t => t.pumps > 0)
      .sort((a, b) => b.pumps - a.pumps);
  }, [services]);

  // Highcharts config for the "Pumps Serviced" column chart — visual match
  // for Dionysus's Highcharts column chart (colorByPoint, data labels,
  // "Numbers" y-axis).
  const dioChartOptions = useMemo(() => ({
    chart: { type: 'column', backgroundColor: 'transparent', height: 300 },
    title: { text: `Pumps Serviced from ${dioChart.rangeStartLabel} to ${dioChart.rangeEndLabel}`, style: { fontSize: '14px', fontWeight: 700, color: '#2d3446' } },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: { categories: dioChart.categories, labels: { style: { fontSize: '11px' } } },
    yAxis: { title: { text: 'Numbers', style: { fontWeight: 700 } } },
    tooltip: {
      headerFormat: '<span style="font-size:11px">Day</span>: <b>{point.x}</b><br/><br/>',
      pointFormat: '<span style="color:{point.color}">Number of Pumps</span>: <b>{point.y}</b><br/>'
    },
    plotOptions: {
      series: { borderWidth: 0, dataLabels: { enabled: true, format: '{point.y}' }, cursor: 'default' }
    },
    series: [{ type: 'column', name: 'day', colorByPoint: true, data: dioChart.counts }]
  }), [dioChart]);

  const saveMonthlyTarget = async () => {
    const val = parseInt(targetInput, 10);
    if (isNaN(val) || val < 0) return;
    setSavingTarget(true);
    try {
      await setDoc(doc(db, 'orgSettings', 'global'), { monthlyServiceTarget: val }, { merge: true });
      setMonthlyTarget(val);
      setEditingTarget(false);
    } catch (e) {
      alert('Failed to save target. Please try again.');
    } finally {
      setSavingTarget(false);
    }
  };

  const calculateStats = (list) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayS = list.filter(s => {
      if (!s.createdAt) return false;
      const d = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
      return d >= today;
    });
    const ids = new Set(todayS.map(s => s.technicianId));
    setStats({
      totalServices: todayS.length,
      completedToday: todayS.filter(s => s.status === 'completed').length,
      inProgress: todayS.filter(s => s.status === 'in_progress').length,
      pending: todayS.filter(s => s.status === 'pending').length,
      activeTechnicians: ids.size
    });
  };

  const getTechnicianPerformance = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayS = services.filter(s => {
      if (!s.createdAt) return false;
      const d = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
      return d >= today;
    });
    const map = {};
    todayS.forEach(s => {
      if (!map[s.technicianId]) map[s.technicianId] = { id: s.technicianId, name: s.technicianName, total: 0, completed: 0, inProgress: 0 };
      map[s.technicianId].total++;
      if (s.status === 'completed') map[s.technicianId].completed++;
      if (s.status === 'in_progress') map[s.technicianId].inProgress++;
    });
    return Object.values(map).sort((a, b) => b.completed - a.completed);
  };

  const formatTime    = (date) => { if (!date) return 'N/A'; const d = date.toDate ? date.toDate() : new Date(date); return d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }); };
  const formatTimeAgo = (date) => { if (!date) return ''; const d = date.toDate ? date.toDate() : new Date(date); const sec = Math.floor((new Date() - d) / 1000); if (sec < 60) return 'Just now'; if (sec < 3600) return `${Math.floor(sec/60)}m ago`; if (sec < 86400) return `${Math.floor(sec/3600)}h ago`; return `${Math.floor(sec/86400)}d ago`; };
  const formatDate    = (date) => { if (!date) return 'N/A'; const d = date.toDate ? date.toDate() : new Date(date); return d.toLocaleString('en-KE'); };
  const getStatusColor = (s) => ({ completed: 'status-completed', in_progress: 'status-progress', pending: 'status-pending' }[s] || 'status-pending');
  const getStatusLabel = (s) => ({ completed: 'Completed', in_progress: 'In Progress', pending: 'Pending' }[s] || 'Unknown');

  const filteredServices = services.filter(s => {
    if (servicesFilter === 'all')         return true;
    if (servicesFilter === 'with_photos') return s.photoCount > 0;
    if (servicesFilter === 'no_photos')   return !s.photoCount || s.photoCount === 0;
    if (servicesFilter === 'flagged')     return s.photoFlags && Object.values(s.photoFlags).some(f => f?.flagged);
    return s.status === servicesFilter;
  });

  const performance    = getTechnicianPerformance();
  const recentServices = services.slice(0, 10);

  // ── All services with location for admin map ──────────────────────────────
  const servicesWithLocation = allServices.filter(s => s.location?.latitude);

  // ── Sub-pages ─────────────────────────────────────────────────────────────
  if (activeTab === 'clock-monitor')     return <SupervisorClockMonitor user={user} onBack={() => setActiveTab('overview')} />;
  if (activeTab === 'journey-monitor')   return <SupervisorJourneyMonitor user={user} onBack={() => setActiveTab('overview')} />;
  if (activeTab === 'outlet-manager')    return <SupervisorOutletManager user={user} onBack={() => setActiveTab('overview')} />;
  if (activeTab === 'spares-monitor')    return <SupervisorSparesMonitor user={user} onBack={() => setActiveTab('overview')} />;
  if (activeTab === 'equipment-manager') return <SupervisorEquipmentManager user={user} onBack={() => setActiveTab('overview')} />;
  if (activeTab === 'intelligence')      return <OperationsIntelligence user={user} onBack={() => setActiveTab('overview')} />;
  
  // ✅ NEW: Supervisor Monitor for admins only
  if (activeTab === 'supervisor-monitor' && isAdmin) 
    return <SupervisorMonitor user={user} onBack={() => setActiveTab('overview')} />;

  // ✅ FIXED: Added user prop to ServicePhotoViewer
  if (viewingPhotos) return <ServicePhotoViewer user={user} service={viewingPhotos} onBack={() => setViewingPhotos(null)} onFlag={async (service, photoKey) => { try { await notifyPhotoFlagged(user, service, photoKey); } catch (err) { console.error('Failed to send flag notification:', err); } }} />;

  // ✅ NEW: Dionysus-style left sidebar nav groups. Same tabs/actions as
  // before (setActiveTab), just reorganized into a grouped sidebar instead
  // of a horizontal scrolling tab strip.
  const dioNavGroups = [
    { label: 'Dashboard', items: [
      { id: 'overview', icon: <FaTachometerAlt />, label: 'Overview' },
    ]},
    { label: 'Technicians', items: [
      { id: 'team', icon: <FaUsers />, label: 'Team' },
      { id: 'clock-monitor', icon: <FaClock />, label: 'Clock' },
      { id: 'journey-monitor', icon: <FaRoute />, label: 'Journeys' },
      ...(isAdmin ? [{ id: 'supervisor-monitor', icon: <FaBullseye />, label: 'Supervisor Monitor' }] : []),
    ]},
    { label: 'Reports', items: [
      { id: 'services', icon: <FaTools />, label: 'Services' },
      { id: 'reports', icon: <FaChartLine />, label: 'Reports' },
      { id: 'intelligence', icon: <FaLightbulb />, label: 'Intelligence' },
    ]},
    { label: 'Pumps & Machines', items: [
      { id: 'equipment-manager', icon: <FaCogs />, label: 'Equipment' },
      { id: 'outlet-manager', icon: <FaStore />, label: 'Outlets' },
      { id: 'transfers', icon: <FaExchangeAlt />, label: 'Transfers' },
    ]},
    { label: 'Spare Parts Inventory', items: [
      { id: 'spares-monitor', icon: <FaWrench />, label: 'Spares' },
      { id: 'spare-parts', icon: <FaBoxes />, label: 'Spare Parts' },
    ]},
    { label: 'Communication', items: [
      { id: 'messaging', icon: <FaComments />, label: 'Messaging' },
    ]},
    { label: 'Account', items: [
      { id: 'settings', icon: <FaCog />, label: 'Settings' },
    ]},
    ...(isAdmin ? [{ label: 'Admin', items: [
      { id: 'org-structure', icon: <FaSitemap />, label: 'Org Structure' },
      { id: 'access-requests', icon: <FaKey />, label: 'Access Requests' },
      { id: 'user-manager', icon: <FaUserShield />, label: 'Users' },
      { id: 'locations-map', icon: <FaMapMarkedAlt />, label: 'Map' },
      { id: 'system-logs', icon: <FaShieldAlt />, label: 'Logs' },
    ]}] : []),
  ];

  const handleNavItemClick = (id) => {
    setActiveTab(id);
    if (window.innerWidth <= 768) setSidebarOpen(false); // collapse after picking a tab on mobile
  };

  const filteredNavGroups = dioNavGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.label.toLowerCase().includes(navSearch.trim().toLowerCase()))
    }))
    .filter(group => group.items.length > 0);

  const activeDioLabel = dioNavGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || 'Dashboard';
  const userInitials = (user?.name || 'U').trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="dio-shell">
      {/* ✅ FIXED: Notification Bell moved to bottom right floating position */}
      <div className="floating-notification-bell">
        <NotificationBell user={user} />
      </div>

      {/* Mobile-only backdrop — click to close the sidebar overlay */}
      {sidebarOpen && (
        <div className="dio-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dio-sidebar ${sidebarOpen ? '' : 'dio-sidebar-collapsed'}`}>
        <div className="dio-sidebar-logo">DILEX</div>
        <div className="dio-profile">
          <div className="dio-profile-avatar">
            <img src="/dionysus-logo.png" alt="Dionysus" className="dio-profile-avatar-img" />
          </div>
          <div className="dio-profile-name">{user?.name || 'User'}</div>
          <div className="dio-profile-title">Dilex Holding Company</div>
        </div>
        <nav className="dio-nav">
          {filteredNavGroups.length === 0 ? (
            <div className="dio-nav-empty">No tabs match "{navSearch}"</div>
          ) : filteredNavGroups.map(group => (
            <div key={group.label}>
              <div className="dio-nav-group-label">{group.label}</div>
              {group.items.map(item => (
                <button
                  key={item.id}
                  className={`dio-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => handleNavItemClick(item.id)}
                >
                  <span className="dio-nav-icon">{item.icon}</span>
                  {item.label}
                  {item.badge && <span className="dio-nav-badge">{item.badge}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="dio-main">
        <div className="dio-topbar">
          <button
            className="dio-topbar-icon"
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            onClick={() => setSidebarOpen(o => !o)}
          >
            <FaBars />
          </button>
          <div className="dio-topbar-search">
            <FaSearch className="dio-search-icon" /> <input
              type="text"
              placeholder="Search tabs..."
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
            />
            {navSearch && (
              <button className="dio-search-clear" onClick={() => setNavSearch('')} aria-label="Clear search"><FaTimes /></button>
            )}
          </div>
          <div className="dio-topbar-right">
            <span style={{ color: '#8b93a7', fontSize: 12 }}>Last updated: {formatTimeAgo(lastUpdate)}</span>
            {onBack && <button className="dio-back-btn" onClick={onBack}><FaArrowLeft style={{ marginRight: 6 }} /> Back</button>}
            <button className="dio-topbar-icon" title="Settings" onClick={() => setActiveTab('settings')} style={{ color: '#fff' }}>
              <FaCog />
            </button>
            {onLogout && (
              <button className="dio-topbar-icon" title="Sign out" onClick={onLogout} style={{ color: '#fff' }}>
                <FaSignOutAlt />
              </button>
            )}
          </div>
        </div>
        <div className="dio-breadcrumb">Home &gt; <span className="active">{activeDioLabel}</span></div>

        <div className="dio-content">
        {loading ? (
          <div className="loading-container"><div className="spinner"></div><p>Loading dashboard...</p></div>
        ) : (
          <>
            {/* ── OVERVIEW ── */}
            {activeTab === 'overview' && (
              <>
                <div className="dio-widget-row">
                  <div className="dio-widget dio-w-orange">
                    <div className="dio-widget-title">Dilex Holding Company</div>
                    <div className="dio-widget-subtitle">Total Pumps Registered</div>
                    <div className="dio-widget-int">{dioStats.totalPumps.toLocaleString()}</div>
                  </div>
                  <div className="dio-widget dio-w-teal">
                    <div className="dio-widget-title">Dilex Holding Company</div>
                    <div className="dio-widget-subtitle">Total outlets Registered</div>
                    <div className="dio-widget-int">{dioStats.totalOutlets.toLocaleString()}</div>
                  </div>
                  <div className="dio-widget dio-w-green dio-w-icon">
                    <div className="dio-widget-icon-left"><FaFireExtinguisher /></div>
                    <div>
                      <div className="dio-widget-int">{dioStats.servicedPumpsRange.toLocaleString()}</div>
                      <div className="dio-widget-title">Serviced Pumps</div>
                      <div className="dio-widget-subtitle" style={{ marginBottom: 0 }}>
                        From: {dioChart.rangeStartLabel} To: {dioChart.rangeEndLabel}
                      </div>
                    </div>
                  </div>
                  <div className="dio-widget dio-w-red dio-w-icon">
                    <div className="dio-widget-icon-left"><FaUser /></div>
                    <div>
                      <div className="dio-widget-int">{dioStats.totalUsers.toLocaleString()}</div>
                      <div className="dio-widget-title">Registered users</div>
                      <div className="dio-widget-subtitle" style={{ marginBottom: 0 }}>On your website</div>
                    </div>
                  </div>
                </div>

                <div className="dio-main-grid">
                  <div>
                    <div className="dio-panel">
                      <div className="dio-panel-heading">
                        <div className="dio-panel-title-box">
                          <h3>Pumps Serviced</h3>
                          <span>Day vs Total Pumps Serviced</span>
                        </div>
                        <div className="dio-panel-controls">
                          <button title="Refresh" onClick={loadDionysusStats}>⟳</button>
                        </div>
                      </div>
                      <div className="dio-panel-body dio-pad-0">
                        <div className="dio-chart-holder">
                          {dioLoading ? (
                            <div className="loading-container"><div className="spinner"></div></div>
                          ) : (
                            <HighchartsReact highcharts={Highcharts} options={dioChartOptions} />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="dio-panel">
                      <div className="dio-panel-heading">
                        <h3 className="dio-panel-title">Today summary Report</h3>
                      </div>
                      <div className="dio-panel-body">
                        <div className="dio-summary-row">
                          <div className="dio-summary-btn dio-sum-green">
                            Pumps Serviced Today
                            <div className="dio-summary-num">{dioStats.todayPumpsServiced}</div>
                          </div>
                          <div className="dio-summary-btn dio-sum-teal">
                            Technicians on Duty
                            <div className="dio-summary-num">{staffOnDuty.onDuty}</div>
                          </div>
                          <div className="dio-summary-btn dio-sum-red">
                            Technicians off Duty
                            <div className="dio-summary-num">{Math.max(staffOnDuty.total - staffOnDuty.onDuty, 0)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="dio-panel">
                    <div className="dio-panel-heading">
                      <div className="dio-panel-title-box">
                        <h3>Technicians</h3>
                        <span>Today- {new Date().toISOString().slice(0, 10)} Pumps Serviced</span>
                      </div>
                    </div>
                    <div className="dio-panel-body dio-pad-0">
                      <table className="dio-table">
                        <thead>
                          <tr>
                            <th>Technician</th>
                            <th>Provider</th>
                            <th>Oulets</th>
                            <th>Pumps</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dioTechToday.length === 0 ? (
                            <tr className="dio-empty-row"><td colSpan={4}>No pumps serviced yet today</td></tr>
                          ) : dioTechToday.map(t => (
                            <tr key={t.id}>
                              <td><strong><span className="dio-tech-link">{t.name}</span></strong></td>
                              <td>Dilex</td>
                              <td><strong>{t.outlets}</strong></td>
                              <td><strong>{t.pumps}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── SERVICES ── */}
            {activeTab === 'services' && (
              <div className="services-tab">
                <div className="section-header">
                  <h2>Service Records</h2>
                  <div className="filter-pills">
                    {['all', 'completed', 'in_progress', 'pending', 'with_photos', 'no_photos', 'flagged'].map(f => (
                      <button key={f} className={`filter-pill ${servicesFilter === f ? 'active' : ''}`} onClick={() => setServicesFilter(f)}>
                        {f === 'all' ? `All (${services.length})` : f === 'with_photos' ? <><FaCamera /> ({services.filter(s => s.photoCount > 0).length})</> : f === 'no_photos' ? <>No <FaCamera /> ({services.filter(s => !s.photoCount).length})</> : f === 'flagged' ? <><FaFlag /> Flagged</> : getStatusLabel(f)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="services-list">
                  {filteredServices.map(service => {
                    const hasFlags = service.photoFlags && Object.values(service.photoFlags).some(f => f?.flagged);
                    return (
                      <div key={service.id} className="service-card" onClick={() => setViewingPhotos(service)} style={{ cursor: 'pointer' }}>
                        <div className="service-header">
                          <div className="service-number">{service.serviceRecordNumber || `SRV-${service.id.slice(-4)}`}</div>
                          <div className={`service-status ${getStatusColor(service.status)}`}>{getStatusLabel(service.status)}</div>
                          {hasFlags && <div className="flag-badge"><FaFlag /> Flagged</div>}
                        </div>
                        <div className="service-body">
                          <div className="service-field"><strong>Outlet:</strong> {service.outletName}</div>
                          <div className="service-field"><strong>Technician:</strong> {service.technicianName}</div>
                          <div className="service-field"><strong>Equipment:</strong> {service.serialNumber}</div>
                          <div className="service-field"><strong>Type:</strong> {service.serviceType}</div>
                          <div className="service-field"><strong>Time:</strong> {formatDate(service.createdAt)}</div>
                          {service.photoCount > 0 && (
                            <div className="photo-count"><FaCamera /> {service.photoCount} photo{service.photoCount !== 1 ? 's' : ''}</div>
                          )}
                        </div>
                        {service.location && (
                          <div className="service-location-row">
                            <span className="location-coords"><FaMapMarkerAlt /> {formatCoordinates(service.location.latitude, service.location.longitude, service.location.accuracy)}</span>
                            <button className="navigate-btn" onClick={() => openInGoogleMaps(service.location.latitude, service.location.longitude, `${service.outletName} - ${service.technicianName}`)}><FaMapMarkedAlt /> Navigate</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── REPORTS ── */}
            {activeTab === 'reports' && <ReportsModule technicians={technicians} />}

            {/* ── TEAM ── */}
            {activeTab === 'team' && (
              <div className="team-tab">
                <div className="section-header"><h2>Team Overview</h2><span className="badge">{technicians.length} technicians</span></div>
                <div className="team-grid">
                  {technicians.map(tech => {
                    const techServices = services.filter(s => s.technicianId === tech.id);
                    const completed    = techServices.filter(s => s.status === 'completed').length;
                    const inProgress   = techServices.filter(s => s.status === 'in_progress').length;
                    const withPhotos   = techServices.filter(s => s.photoCount > 0).length;
                    return (
                      <div key={tech.id} className="team-member-card">
                        <div className="member-avatar">{tech.name.charAt(0).toUpperCase()}</div>
                        <div className="member-info"><h3>{tech.name}</h3><p className="member-phone">{tech.phone}</p></div>
                        <div className="member-stats">
                          <div className="member-stat"><span className="stat-number">{techServices.length}</span><span className="stat-text">Total</span></div>
                          <div className="member-stat"><span className="stat-number success-text">{completed}</span><span className="stat-text">Done</span></div>
                          <div className="member-stat"><span className="stat-number warning-text">{inProgress}</span><span className="stat-text">Active</span></div>
                          <div className="member-stat"><span className="stat-number info-text">{withPhotos}</span><span className="stat-text"><FaCamera /></span></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── MESSAGING TAB ── */}
            {activeTab === 'messaging' && (
              <div>
                <SupervisorMessaging user={user} technicians={technicians} />
              </div>
            )}

            {/* ── ADMIN: ORG STRUCTURE ── */}
            {activeTab === 'org-structure' && isAdmin && (
              <OrgStructure user={user} />
            )}

            {/* ── ADMIN: ACCESS REQUESTS ── */}
            {activeTab === 'access-requests' && isAdmin && (
              <AccessRequests user={user} />
            )}

            {/* ── SUPERVISOR+: EQUIPMENT TRANSFERS ── */}
            {activeTab === 'transfers' && (
              <TransferManager user={user} />
            )}

            {/* ── SUPERVISOR+: SPARE PARTS INVENTORY ── */}
            {activeTab === 'spare-parts' && (
              <SparePartsInventory user={user} technicians={technicians} />
            )}

            {/* ── ADMIN: USER MANAGER ── */}
            {activeTab === 'user-manager' && isAdmin && (
              <div>
                <div className="section-header" style={{ marginBottom: 20 }}>
                  <h2>
                    {isSuperAdmin(user) ? <><FaStar /> User Management (Super Admin)</> : <><FaCrown /> User Management</>}
                  </h2>
                  <span className={isSuperAdmin(user) ? 'badge-role-super' : 'badge-role-admin'}>
                    {isSuperAdmin(user) ? 'Super Admin Only' : 'Admin Only'}
                  </span>
                </div>
                <AdminUserManager user={user} />
              </div>
            )}

            {/* ── ADMIN: ALL LOCATIONS ── */}
            {activeTab === 'locations-map' && isAdmin && (
              <div>
                <div className="section-header" style={{ marginBottom: 20 }}>
                  <h2><FaMapMarkerAlt /> All Technician Locations</h2>
                  <span className="badge-admin-only">Admin Only</span>
                </div>
                <div className="map-hint">
                  Showing last recorded location from all {servicesWithLocation.length} service records with GPS data
                </div>
                <div className="map-list">
                  {servicesWithLocation.length === 0 ? (
                    <div className="empty-state"><div className="empty-icon"><FaMapMarkerAlt /></div><h3>No location data</h3><p>Location data will appear as technicians record services</p></div>
                  ) : servicesWithLocation.map(service => (
                    <div key={service.id} className="map-item">
                      <div className="map-item-icon"><FaMapMarkerAlt /></div>
                      <div className="map-item-body">
                        <div className="map-item-name">{service.technicianName}</div>
                        <div className="map-item-meta">
                          <FaStore /> {service.outletName} • {formatTimeAgo(service.createdAt)}
                        </div>
                        <div className="map-item-coords">
                          {service.location.latitude.toFixed(6)}, {service.location.longitude.toFixed(6)}
                          {service.location.accuracy && ` ±${Math.round(service.location.accuracy)}m`}
                        </div>
                      </div>
                      <button
                        className="map-open-btn"
                        onClick={() => openInGoogleMaps(service.location.latitude, service.location.longitude, `${service.technicianName} — ${service.outletName}`)}>
                        <FaMapMarkedAlt /> Open Map
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── ADMIN: SYSTEM LOGS ── */}
            {activeTab === 'system-logs' && isAdmin && (
              <div>
                <div className="section-header" style={{ marginBottom: 20 }}>
                  <h2><FaShieldAlt /> System Activity Logs</h2>
                  <div className="logs-header-actions">
                    <span className="badge-admin-only">Admin Only</span>
                    <button
                      className="logs-refresh-btn"
                      onClick={loadLogs}>
                      <FaSync /> Refresh
                    </button>
                  </div>
                </div>
                {logsLoading ? (
                  <div className="logs-loading">Loading logs...</div>
                ) : logs.length === 0 ? (
                  <div className="empty-state"><div className="empty-icon"><FaClipboardList /></div><h3>No logs found</h3><p>Activity logs will appear here</p></div>
                ) : (
                  <div className="logs-list">
                    {logs.map(log => (
                      <div key={log.id} className="log-item">
                        <div className="log-icon">
                          {log.action?.includes('login') ? <FaKey /> : log.action?.includes('service') ? <FaWrench /> : log.action?.includes('location') ? <FaMapMarkerAlt /> : <FaClipboardList />}
                        </div>
                        <div className="log-body">
                          <div className="log-title">
                            {log.userName || log.userId || 'Unknown'} — <span className="log-action">{log.action}</span>
                          </div>
                          {log.description && <div className="log-description">{log.description}</div>}
                          <div className="log-time">{formatDate(log.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── SETTINGS ── */}
            {activeTab === 'settings' && (
              <SettingsModule
                user={user}
                onBack={() => setActiveTab('overview')}
                onUserUpdate={onUserUpdate}
              />
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}

export default SupervisorDashboard;
